import { useCallback, useEffect, useRef, useState } from 'react';
import type { Note } from '@voice/shared';
import { supabase } from '../services/supabase';
import { getCachedNotes, setCachedNotes } from '../services/noteCache';
import { useNetwork } from './useNetwork';
import { useAuth } from './AuthContext';

export interface UseNotesReturn {
  notes: Note[];
  loading: boolean;
  error: string | null;
  fromCache: boolean;
  refresh: () => Promise<void>;
}

export function useNotes(): UseNotesReturn {
  const { user } = useAuth();
  const { online } = useNetwork();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const fetchedOnce = useRef(false);

  const userId = user?.id;

  const loadFromCache = useCallback(async () => {
    if (!userId) return;
    try {
      const cached = await getCachedNotes(userId);
      if (cached.length > 0) {
        setNotes(cached);
        setFromCache(true);
      }
    } catch {
      // Cache read failure is non-fatal
    }
  }, [userId]);

  const fetchFromSupabase = useCallback(async () => {
    if (!userId) return;
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (fetchError) throw new Error(fetchError.message);

      const freshNotes = (data ?? []) as Note[];
      setNotes(freshNotes);
      setFromCache(false);
      fetchedOnce.current = true;

      // Persist to IndexedDB in background
      await setCachedNotes(userId, freshNotes);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load notes');
    }
  }, [userId]);

  const refresh = useCallback(async () => {
    if (!online) return;
    setLoading(true);
    await fetchFromSupabase();
    setLoading(false);
  }, [online, fetchFromSupabase]);

  // Initial load: cache first, then network
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function init() {
      // 1. Show cached data immediately
      await loadFromCache();

      // 2. If online, fetch fresh data
      if (online) {
        await fetchFromSupabase();
      }

      if (!cancelled) setLoading(false);
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [userId, online, loadFromCache, fetchFromSupabase]);

  // When coming back online after being offline, refresh
  useEffect(() => {
    if (online && userId && fetchedOnce.current) {
      fetchFromSupabase();
    }
  }, [online, userId, fetchFromSupabase]);

  // Poll while any note is in a transient state (transcribing/processing)
  useEffect(() => {
    const hasTransient = notes.some(
      (n) => n.status === 'transcribing' || n.status === 'processing'
    );

    if (!hasTransient || !online || !userId) return;

    const interval = setInterval(() => {
      fetchFromSupabase();
    }, 3000);

    return () => clearInterval(interval);
  }, [notes, online, userId, fetchFromSupabase]);

  return { notes, loading, error, fromCache, refresh };
}
